import type {
  ApiResponse,
  ExportReportCsvRequest,
  ExportReportCsvResponse,
} from '@shared/types';
import { REPORTS_IPC_CHANNELS } from '../../../utils/constants';
import type {
  DepartmentSalesSummary,
  ItemSalesSummaryDetails,
  ItemSalesSummaryView,
  KotTrackingRow,
  OrderSettlementView,
  PaymentMethodDailySummary,
  PaymentMethodSalesSummary,
  SalesItemView,
  SalesReportCreditorView,
  SalesReportDiscountView,
  SalesReportOrderDetails,
  SalesReportOrderView,
  SalesReportSummary,
  SalesReportUserSummary,
} from '../types/report';
import type {
  CachedItemSalesDetails,
  CachedOrderDetails,
  CachedReportRows,
  ReportFilterOption,
  ReportQueryParams,
} from '../types/reports';
import { supabase } from './supabase';

const REPORT_CACHE_TTL_MS = 5 * 60 * 1000;
const DEFAULT_REPORT_PAGE_SIZE = 50;

const reportCache = new Map<string, CachedReportRows<unknown>>();
const inFlightCache = new Map<
  string,
  Promise<ApiResponse<CachedReportRows<unknown>>>
>();
const filterOptionsCache = new Map<
  string,
  { data: ReportFilterOption[]; fetchedAt: number }
>();
const orderDetailsCache = new Map<string, CachedOrderDetails>();
const orderDetailsInFlight = new Map<
  string,
  Promise<ApiResponse<CachedOrderDetails>>
>();
const itemSalesDetailsCache = new Map<string, CachedItemSalesDetails>();
const itemSalesDetailsInFlight = new Map<
  string,
  Promise<ApiResponse<CachedItemSalesDetails>>
>();

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const escapeLikePattern = (value: string): string =>
  value.replace(/[%_]/g, (match) => `\\${match}`);

const applyScope = <T extends { eq: Function }>(
  query: T,
  companyId: number,
  outletId: string | null,
) => {
  if (outletId) {
    return query.eq('outlet_id', outletId);
  }

  return query.eq('company_id', companyId);
};

const mapUserSummary = (value: any): SalesReportUserSummary => ({
  id: value?.id ?? null,
  first_name: value?.first_name ?? null,
  last_name: value?.last_name ?? null,
  outlet_id: value?.outlet_id ?? null,
});

const mapSummary = (value: any): SalesReportSummary => ({
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  settled_order_count: toNumber(value.settled_order_count),
  settlement_count: toNumber(value.settlement_count),
  gross_sale: toNumber(value.gross_sale),
  total_discount: toNumber(value.total_discount),
  total_sale: toNumber(value.total_sale),
  total_settlement_amount: toNumber(value.total_settlement_amount),
  average_bill_amount: toNumber(value.average_bill_amount),
});

const mapPaymentMethodSummary = (value: any): PaymentMethodSalesSummary => ({
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  payment_method_id: toNumber(value.payment_method_id),
  payment_method_name: value.payment_method_name || '',
  settlement_count: toNumber(value.settlement_count),
  settled_order_count: toNumber(value.settled_order_count),
  total_amount: toNumber(value.total_amount),
});

const fetchScopedBusinessDatePage = async (
  params: ReportQueryParams,
): Promise<ApiResponse<{ dates: string[]; hasMore: boolean }>> => {
  const offset = params.offset ?? 0;
  const limit = params.limit ?? DEFAULT_REPORT_PAGE_SIZE;

  if (params.outletId) {
    const { data, error } = await applyScope(
      supabase
        .from('sales_summary_view')
        .select('sale_date')
        .gte('sale_date', params.startDate)
        .lte('sale_date', params.endDate)
        .order('sale_date', { ascending: false }),
      params.companyId,
      params.outletId,
    ).range(offset, offset + limit);

    if (error) {
      return {
        success: false,
        error: error.message,
      };
    }

    const dates = ((data || []) as Array<{ sale_date: string | null }>)
      .map((row: { sale_date: string | null }) => row.sale_date as string)
      .filter(Boolean)
      .slice(0, limit);

    return {
      success: true,
      data: {
        dates,
        hasMore: (data || []).length > limit,
      },
    };
  }

  const targetCount = offset + limit + 1;
  const uniqueDates: string[] = [];
  const seen = new Set<string>();
  let cursor = 0;
  let hasUnderlyingMore = true;
  const chunkSize = Math.max(limit * 6, 100);

  while (uniqueDates.length < targetCount && hasUnderlyingMore) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await applyScope(
      supabase
        .from('sales_summary_view')
        .select('sale_date')
        .gte('sale_date', params.startDate)
        .lte('sale_date', params.endDate)
        .order('sale_date', { ascending: false }),
      params.companyId,
      null,
    ).range(cursor, cursor + chunkSize - 1);

    if (error) {
      return {
        success: false,
        error: error.message,
      };
    }

    const rows = (data || []) as Array<{ sale_date: string | null }>;
    if (rows.length < chunkSize) {
      hasUnderlyingMore = false;
    }

    rows.forEach((row: { sale_date: string | null }) => {
      const saleDate = row.sale_date as string | undefined;
      if (!saleDate || seen.has(saleDate)) return;
      seen.add(saleDate);
      uniqueDates.push(saleDate);
    });

    if (rows.length === 0) {
      break;
    }

    cursor += rows.length;
  }

  return {
    success: true,
    data: {
      dates: uniqueDates.slice(offset, offset + limit),
      hasMore: uniqueDates.length > offset + limit || hasUnderlyingMore,
    },
  };
};

const loadPaymentMethodDailySummaryRows = async (
  params: ReportQueryParams,
): Promise<
  ApiResponse<{ rows: PaymentMethodDailySummary[]; hasMore: boolean }>
> => {
  const datesResponse = await fetchScopedBusinessDatePage(params);

  if (!datesResponse.success || !datesResponse.data) {
    return {
      success: false,
      error: datesResponse.error || 'Failed to load sales summary dates',
    };
  }

  const { dates, hasMore } = datesResponse.data;
  if (dates.length === 0) {
    return {
      success: true,
      data: {
        rows: [],
        hasMore: false,
      },
    };
  }

  const { data, error } = await applyScope(
    supabase
      .from('payment_method_sales_summary_view')
      .select('*')
      .in('sale_date', dates)
      .order('sale_date', { ascending: false })
      .order('payment_method_name', { ascending: true }),
    params.companyId,
    params.outletId,
  );

  if (error) {
    return {
      success: false,
      error: error.message,
    };
  }

  const rawRows: PaymentMethodSalesSummary[] = (data || []).map(
    mapPaymentMethodSummary,
  );
  const rowsByDate = new Map<string, PaymentMethodDailySummary>(
    dates.map((saleDate) => [
      saleDate,
      {
        sale_date: saleDate,
        total_amount: 0,
        settlement_count: 0,
        settled_order_count: 0,
        method_totals: {},
      },
    ]),
  );

  rawRows.forEach((row: PaymentMethodSalesSummary) => {
    const summary = rowsByDate.get(row.sale_date);
    if (!summary) return;

    summary.total_amount += row.total_amount;
    summary.settlement_count += row.settlement_count;
    summary.settled_order_count += row.settled_order_count;
    summary.method_totals[row.payment_method_name] =
      (summary.method_totals[row.payment_method_name] || 0) + row.total_amount;
  });

  return {
    success: true,
    data: {
      rows: dates
        .map((saleDate) => rowsByDate.get(saleDate))
        .filter((row): row is PaymentMethodDailySummary => Boolean(row)),
      hasMore,
    },
  };
};

const mapDepartmentSummary = (value: any): DepartmentSalesSummary => ({
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  department_id: value.department_id ?? null,
  department_name: value.department_name || 'Unassigned',
  item_count: toNumber(value.item_count),
  quantity_sold: toNumber(value.quantity_sold),
  gross_sale: toNumber(value.gross_sale),
  total_discount: toNumber(value.total_discount),
  total_sale: toNumber(value.total_sale),
});

const mapSettlement = (value: any): OrderSettlementView => ({
  id: value.id,
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  waiter_id: value.waiter_id ?? null,
  waiter_name: value.waiter_name ?? null,
  amount: toNumber(value.amount),
  payment_method_id: toNumber(value.payment_method_id),
  payment_method_name: value.payment_method_name || '',
  notes: value.notes ?? null,
  order_total_amount: toNumber(value.order_total_amount),
  order_discount_amount: toNumber(value.order_discount_amount),
  order_net_amount: toNumber(value.order_net_amount),
  order_balance_amount: toNumber(value.order_balance_amount),
  created_by: mapUserSummary(value.created_by),
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapItem = (value: any): SalesItemView => ({
  id: value.id,
  item_name: value.item_name,
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  menu_item_id: value.menu_item_id,
  quantity: toNumber(value.quantity),
  unit_price: toNumber(value.unit_price),
  total_amount: toNumber(value.total_amount),
  allocated_discount: toNumber(value.allocated_discount),
  net_amount: toNumber(value.net_amount),
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  notes: value.notes ?? null,
  status: value.status || '',
  kot_id: toNumber(value.kot_id),
  added_by: mapUserSummary(value.added_by),
  department_id: value.department_id ?? null,
  department_name: value.department_name ?? null,
  category_id: value.category_id ?? null,
  category_name: value.category_name ?? null,
  waiter_id: value.waiter_id ?? null,
  waiter_name: value.waiter_name ?? null,
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapItemSummary = (value: any): ItemSalesSummaryView => ({
  id: value.id,
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  menu_item_id: value.menu_item_id,
  item_name: value.item_name || '',
  department_id: value.department_id ?? null,
  department_name: value.department_name || 'Unassigned',
  category_id: value.category_id ?? null,
  category_name: value.category_name || 'Unassigned',
  unit_price: toNumber(value.unit_price),
  order_count: toNumber(value.order_count),
  quantity_sold: toNumber(value.quantity_sold),
  gross_amount: toNumber(value.gross_amount),
});

const mapDiscount = (value: any): SalesReportDiscountView => ({
  id: value.id,
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  waiter_id: value.waiter_id ?? null,
  waiter_name: value.waiter_name ?? null,
  amount: toNumber(value.amount),
  notes: value.notes ?? null,
  added_by: mapUserSummary(value.added_by),
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapCreditor = (value: any): SalesReportCreditorView => ({
  id: value.id,
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  client_email: value.client_email ?? null,
  client_contact: value.client_contact ?? null,
  amount: toNumber(value.amount),
  payment_method_id: toNumber(value.payment_method_id),
  payment_method_name: value.payment_method_name || '',
  notes: value.notes ?? null,
  created_by: mapUserSummary(value.created_by),
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapOrder = (value: any): SalesReportOrderView => ({
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  status_id: toNumber(value.status_id),
  status_name: value.status_name || '',
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  waiter_id: value.waiter_id ?? null,
  waiter_name: value.waiter_name ?? null,
  item_count: toNumber(value.item_count),
  pending_item_count: toNumber(value.pending_item_count),
  discount_amount: toNumber(value.discount_amount),
  total_amount: toNumber(value.total_amount),
  net_amount: toNumber(value.net_amount),
  settlement_total: toNumber(value.settlement_total),
  settlement_count: toNumber(value.settlement_count),
  balance_amount: toNumber(value.balance_amount),
  is_fully_settled: Boolean(value.is_fully_settled),
  created_by: mapUserSummary(value.created_by),
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapKotTrackingRow = (value: any): KotTrackingRow => ({
  order_id: value.order_id,
  kot_id: toNumber(value.kot_id),
  physical_kot_number: value.physical_kot_number ?? null,
  physical_kot_recorded_by: value.physical_kot_recorded_by ?? null,
  recorded_by_name: value.recorded_by_name || null,
  outlet_id: value.outlet_id,
  outlet_name: value.outlet_name || '',
  company_id: toNumber(value.company_id),
  bill_number: toNumber(value.bill_number),
  mode: value.mode,
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  order_date: value.order_date,
  waiter_id: value.waiter_id ?? null,
  waiter_name: value.waiter_name ?? null,
  confirmed_at: value.confirmed_at,
  item_count: toNumber(value.item_count),
  total_amount: toNumber(value.total_amount),
});

const buildCacheKey = (reportKey: string, params: ReportQueryParams): string =>
  [
    reportKey,
    params.companyId,
    params.outletId || 'all-outlets',
    params.startDate,
    params.endDate,
    params.search || '',
    JSON.stringify(params.filters || {}),
    params.offset ?? 0,
    params.limit ?? DEFAULT_REPORT_PAGE_SIZE,
  ].join('::');

const resolveCachedValue = <T>(
  cacheKey: string,
  forceRefresh?: boolean,
): CachedReportRows<T> | null => {
  if (forceRefresh) return null;

  const cached = reportCache.get(cacheKey) as CachedReportRows<T> | undefined;
  if (!cached) return null;

  if (Date.now() - cached.fetchedAt > REPORT_CACHE_TTL_MS) {
    reportCache.delete(cacheKey);
    return null;
  }

  return {
    ...cached,
    fromCache: true,
  };
};

const fetchCachedRows = async <T>(
  cacheKey: string,
  forceRefresh: boolean | undefined,
  loader: () => Promise<ApiResponse<{ rows: T[]; hasMore: boolean }>>,
): Promise<ApiResponse<CachedReportRows<T>>> => {
  const cached = resolveCachedValue<T>(cacheKey, forceRefresh);
  if (cached) {
    return {
      success: true,
      data: cached,
    };
  }

  const existingRequest = inFlightCache.get(cacheKey);
  if (existingRequest && !forceRefresh) {
    return existingRequest as Promise<ApiResponse<CachedReportRows<T>>>;
  }

  const request = (async () => {
    const response = await loader();
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load report data',
      };
    }

    const data: CachedReportRows<T> = {
      rows: response.data.rows,
      hasMore: response.data.hasMore,
      fetchedAt: Date.now(),
      fromCache: false,
    };

    reportCache.set(cacheKey, data as CachedReportRows<unknown>);

    return {
      success: true,
      data,
    };
  })();

  inFlightCache.set(
    cacheKey,
    request as Promise<ApiResponse<CachedReportRows<unknown>>>,
  );

  try {
    return await request;
  } finally {
    inFlightCache.delete(cacheKey);
  }
};

const resolveCachedOrderDetails = (
  cacheKey: string,
  forceRefresh?: boolean,
): CachedOrderDetails | null => {
  if (forceRefresh) return null;

  const cached = orderDetailsCache.get(cacheKey);
  if (!cached) return null;

  if (Date.now() - cached.fetchedAt > REPORT_CACHE_TTL_MS) {
    orderDetailsCache.delete(cacheKey);
    return null;
  }

  return {
    ...cached,
    fromCache: true,
  };
};

const fetchCachedOrderDetails = async (
  cacheKey: string,
  forceRefresh: boolean | undefined,
  loader: () => Promise<ApiResponse<SalesReportOrderDetails>>,
): Promise<ApiResponse<CachedOrderDetails>> => {
  const cached = resolveCachedOrderDetails(cacheKey, forceRefresh);
  if (cached) {
    return {
      success: true,
      data: cached,
    };
  }

  const existingRequest = orderDetailsInFlight.get(cacheKey);
  if (existingRequest && !forceRefresh) {
    return existingRequest;
  }

  const request = (async () => {
    const response = await loader();
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load order details',
      };
    }

    const data: CachedOrderDetails = {
      data: response.data,
      fetchedAt: Date.now(),
      fromCache: false,
    };

    orderDetailsCache.set(cacheKey, data);

    return {
      success: true,
      data,
    };
  })();

  orderDetailsInFlight.set(cacheKey, request);

  try {
    return await request;
  } finally {
    orderDetailsInFlight.delete(cacheKey);
  }
};

const resolveCachedItemSalesDetails = (
  cacheKey: string,
  forceRefresh?: boolean,
): CachedItemSalesDetails | null => {
  if (forceRefresh) return null;

  const cached = itemSalesDetailsCache.get(cacheKey);
  if (!cached) return null;

  if (Date.now() - cached.fetchedAt > REPORT_CACHE_TTL_MS) {
    itemSalesDetailsCache.delete(cacheKey);
    return null;
  }

  return {
    ...cached,
    fromCache: true,
  };
};

const fetchCachedItemSalesDetails = async (
  cacheKey: string,
  forceRefresh: boolean | undefined,
  loader: () => Promise<ApiResponse<ItemSalesSummaryDetails>>,
): Promise<ApiResponse<CachedItemSalesDetails>> => {
  const cached = resolveCachedItemSalesDetails(cacheKey, forceRefresh);
  if (cached) {
    return {
      success: true,
      data: cached,
    };
  }

  const existingRequest = itemSalesDetailsInFlight.get(cacheKey);
  if (existingRequest && !forceRefresh) {
    return existingRequest;
  }

  const request = (async () => {
    const response = await loader();
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load item sales details',
      };
    }

    const data: CachedItemSalesDetails = {
      data: response.data,
      fetchedAt: Date.now(),
      fromCache: false,
    };

    itemSalesDetailsCache.set(cacheKey, data);

    return {
      success: true,
      data,
    };
  })();

  itemSalesDetailsInFlight.set(cacheKey, request);

  try {
    return await request;
  } finally {
    itemSalesDetailsInFlight.delete(cacheKey);
  }
};

const loadReportRows = async <T>({
  viewName,
  params,
  mapRow,
  orderBy,
}: {
  viewName: string;
  params: ReportQueryParams;
  mapRow: (value: any) => T;
  orderBy: Array<{ column: string; ascending: boolean }>;
}): Promise<ApiResponse<{ rows: T[]; hasMore: boolean }>> => {
  const offset = params.offset ?? 0;
  const limit = params.limit ?? DEFAULT_REPORT_PAGE_SIZE;

  let query = applyScope(
    supabase
      .from(viewName)
      .select('*')
      .gte('sale_date', params.startDate)
      .lte('sale_date', params.endDate),
    params.companyId,
    params.outletId,
  );

  orderBy.forEach((entry) => {
    query = query.order(entry.column, { ascending: entry.ascending });
  });

  const { data, error } = await query.range(offset, offset + limit);

  if (error) {
    return {
      success: false,
      error: error.message,
    };
  }

  return {
    success: true,
    data: {
      rows: (data || []).slice(0, limit).map(mapRow),
      hasMore: (data || []).length > limit,
    },
  };
};

const loadItemSalesSummaryRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<{ rows: ItemSalesSummaryView[]; hasMore: boolean }>> => {
  const offset = params.offset ?? 0;
  const limit = params.limit ?? DEFAULT_REPORT_PAGE_SIZE;
  const departmentId =
    typeof params.filters?.departmentId === 'string'
      ? params.filters.departmentId
      : null;
  const settlementTypeIds = Array.isArray(params.filters?.settlementTypeIds)
    ? (params.filters.settlementTypeIds as string[]).filter(Boolean)
    : [];
  const normalizedSearch = params.search?.trim();

  // When a settlement-type filter is active, query the sibling view that
  // fans an item out across every distinct payment method used on its
  // order (see item_sales_summary_by_settlement_view migration) instead of
  // the default unfiltered view — keeps the no-filter report's row shape
  // unchanged when no settlement filter is selected.
  const viewName = settlementTypeIds.length
    ? 'item_sales_summary_by_settlement_view'
    : 'item_sales_summary_view';

  let query = applyScope(
    supabase
      .from(viewName)
      .select('*')
      .gte('sale_date', params.startDate)
      .lte('sale_date', params.endDate)
      .order('sale_date', { ascending: false })
      .order('item_name', { ascending: true })
      .order('unit_price', { ascending: true }),
    params.companyId,
    params.outletId,
  );

  if (departmentId) {
    query = query.eq('department_id', departmentId);
  }

  if (settlementTypeIds.length) {
    query = query.in(
      'payment_method_id',
      settlementTypeIds.map((id) => Number(id)),
    );
  }

  if (normalizedSearch) {
    const escapedSearch = escapeLikePattern(
      normalizedSearch.replace(/,/g, ' '),
    );
    query = query.or(
      [
        `item_name.ilike.%${escapedSearch}%`,
        `department_name.ilike.%${escapedSearch}%`,
        `category_name.ilike.%${escapedSearch}%`,
        `outlet_name.ilike.%${escapedSearch}%`,
      ].join(','),
    );
  }

  const { data, error } = await query.range(offset, offset + limit);

  if (error) {
    return {
      success: false,
      error: error.message,
    };
  }

  return {
    success: true,
    data: {
      rows: (data || []).slice(0, limit).map(mapItemSummary),
      hasMore: (data || []).length > limit,
    },
  };
};

const STATEMENT_TIMEOUT_CODE = '57014';

// Postgres cancels report queries that exceed the role's statement_timeout;
// its raw message ("canceling statement due to statement timeout") means
// nothing to staff.
const toReportQueryError = (error: {
  code?: string;
  message: string;
}): string =>
  error.code === STATEMENT_TIMEOUT_CODE ||
  /statement timeout/i.test(error.message)
    ? 'This search took too long. Try a shorter date range or a more specific search.'
    : error.message;

// PostgREST filter values are quoted so commas, parentheses and dots in a
// search can't break the `or=(...)` logic tree.
const quoteFilterValue = (value: string): string =>
  `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

const ORDER_SEARCH_TEXT_COLUMNS = [
  'table_name',
  'section_name',
  'client_name',
  'waiter_name',
  'outlet_name',
  'status_name',
] as const;

/**
 * Builds the `.or()` filter for the Orders report search. A number (with or
 * without a leading "#") matches a bill number or net amount exactly —
 * PostgREST filters can't cast, so `bill_number::text.ilike` is not an
 * option. Mirrored in SQL by get_orders_report_summary so the stat cards
 * and the table always count the same orders.
 */
const buildOrdersSearchFilter = (search?: string): string | null => {
  const term = search?.trim();
  if (!term) return null;

  const filters: string[] = [];
  const billTerm = term.replace(/^#+/, '');
  // Capped at 9 digits so the value always fits the integer bill_number column.
  if (/^\d{1,9}$/.test(billTerm)) {
    filters.push(`bill_number.eq.${billTerm}`, `net_amount.eq.${billTerm}`);
  }

  const pattern = quoteFilterValue(`%${term}%`);
  ORDER_SEARCH_TEXT_COLUMNS.forEach((column) => {
    filters.push(`${column}.ilike.${pattern}`);
  });

  return filters.join(',');
};

export const fetchOrdersReportRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<SalesReportOrderView>>> => {
  const clientId =
    typeof params.filters?.clientId === 'string'
      ? params.filters.clientId
      : null;
  const statusId =
    typeof params.filters?.statusId === 'string'
      ? params.filters.statusId
      : null;

  return fetchCachedRows(
    buildCacheKey('orders', params),
    params.forceRefresh,
    async () => {
      const offset = params.offset ?? 0;
      const limit = params.limit ?? DEFAULT_REPORT_PAGE_SIZE;

      let query = applyScope(
        supabase
          .from('sales_orders_view')
          .select('*')
          .gte('sale_date', params.startDate)
          .lte('sale_date', params.endDate),
        params.companyId,
        params.outletId,
      );

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      if (statusId) {
        query = query.eq('status_id', Number(statusId));
      }

      const searchFilter = buildOrdersSearchFilter(params.search);
      if (searchFilter) {
        query = query.or(searchFilter);
      }

      query = query
        .order('sale_date', { ascending: false })
        .order('created_at', { ascending: false });

      const { data, error } = await query.range(offset, offset + limit);

      if (error) {
        return { success: false, error: toReportQueryError(error) };
      }

      return {
        success: true,
        data: {
          rows: (data || []).slice(0, limit).map(mapOrder),
          hasMore: (data || []).length > limit,
        },
      };
    },
  );
};

export interface OrdersReportSummary {
  orderCount: number;
  pendingItemCount: number;
  fullySettledCount: number;
  netAmount: number;
}

const ordersSummaryCache = new Map<
  string,
  { data: OrdersReportSummary; fetchedAt: number }
>();
const ordersSummaryInFlight = new Map<
  string,
  Promise<ApiResponse<OrdersReportSummary>>
>();

/**
 * Totals for the whole selected period (not just the loaded page), using
 * the same scope, filters and search as fetchOrdersReportRows.
 */
export const fetchOrdersReportSummary = async (
  params: ReportQueryParams,
): Promise<ApiResponse<OrdersReportSummary>> => {
  const clientId =
    typeof params.filters?.clientId === 'string'
      ? params.filters.clientId
      : null;
  const statusId =
    typeof params.filters?.statusId === 'string'
      ? params.filters.statusId
      : null;

  const cacheKey = buildCacheKey('ordersSummary', {
    ...params,
    offset: 0,
    limit: 0,
  });
  const cached = ordersSummaryCache.get(cacheKey);
  if (
    cached &&
    !params.forceRefresh &&
    Date.now() - cached.fetchedAt <= REPORT_CACHE_TTL_MS
  ) {
    return { success: true, data: cached.data };
  }

  // Identical concurrent requests share one RPC call. A forced refresh always
  // goes to the server rather than joining a request that may predate it.
  const inFlight = ordersSummaryInFlight.get(cacheKey);
  if (inFlight && !params.forceRefresh) {
    return inFlight;
  }

  const request = (async (): Promise<ApiResponse<OrdersReportSummary>> => {
    const { data, error } = await supabase.rpc('get_orders_report_summary', {
      p_company_id: params.companyId,
      p_outlet_id: params.outletId,
      p_start_date: params.startDate,
      p_end_date: params.endDate,
      p_client_id: clientId || null,
      p_status_id: statusId ? Number(statusId) : null,
      p_search: params.search?.trim() || null,
    });

    if (error) {
      return { success: false, error: toReportQueryError(error) };
    }

    const row = (Array.isArray(data) ? data[0] : data) ?? {};
    const summary: OrdersReportSummary = {
      orderCount: toNumber(row.order_count),
      pendingItemCount: toNumber(row.pending_item_count),
      fullySettledCount: toNumber(row.fully_settled_count),
      netAmount: toNumber(row.net_amount),
    };

    ordersSummaryCache.set(cacheKey, { data: summary, fetchedAt: Date.now() });
    return { success: true, data: summary };
  })();

  ordersSummaryInFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    if (ordersSummaryInFlight.get(cacheKey) === request) {
      ordersSummaryInFlight.delete(cacheKey);
    }
  }
};

export const fetchKotTrackingRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<KotTrackingRow>>> =>
  fetchCachedRows(
    buildCacheKey('kotTracking', params),
    params.forceRefresh,
    async () => {
      const offset = params.offset ?? 0;
      const limit = params.limit ?? DEFAULT_REPORT_PAGE_SIZE;

      let query = applyScope(
        supabase
          .from('kot_tracking_view')
          .select('*')
          .gte('order_date', params.startDate)
          .lte('order_date', params.endDate),
        params.companyId,
        params.outletId,
      );

      const searchTerm = params.search?.trim();
      if (searchTerm) {
        const escapedSearch = escapeLikePattern(searchTerm.replace(/,/g, ' '));
        // "#128" should still match kot/bill numbers by their digits alone.
        const numericTerm = escapedSearch.replace(/^#+/, '') || escapedSearch;
        // kot_id_search/bill_number_search are pre-cast TEXT columns on the
        // view (see 20260805090300_create_kot_tracking_view.sql) — PostgREST's
        // `or=(...)` logic-tree grammar doesn't support `::type` casts inside
        // it, so the cast can't happen here in the filter string itself.
        query = query.or(
          [
            `kot_id_search.ilike.%${numericTerm}%`,
            `physical_kot_number.ilike.%${escapedSearch}%`,
            `bill_number_search.ilike.%${numericTerm}%`,
            `table_name.ilike.%${escapedSearch}%`,
            `section_name.ilike.%${escapedSearch}%`,
            `waiter_name.ilike.%${escapedSearch}%`,
            `recorded_by_name.ilike.%${escapedSearch}%`,
          ].join(','),
        );
      }

      query = query
        .order('confirmed_at', { ascending: false })
        .order('kot_id', { ascending: false });

      const { data, error } = await query.range(offset, offset + limit);

      if (error) {
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          rows: (data || []).slice(0, limit).map(mapKotTrackingRow),
          hasMore: (data || []).length > limit,
        },
      };
    },
  );

export const fetchKotTrackingItems = async (
  orderId: string,
  kotId: number,
): Promise<ApiResponse<SalesItemView[]>> => {
  try {
    const { data, error } = await supabase
      .from('sales_items_view')
      .select('*')
      .eq('order_id', orderId)
      .eq('kot_id', kotId)
      .order('created_at', { ascending: true });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data: (data || []).map(mapItem) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch KOT items',
    };
  }
};

export const fetchSettlementsReportRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<OrderSettlementView>>> =>
  fetchCachedRows(
    buildCacheKey('settlements', params),
    params.forceRefresh,
    () =>
      loadReportRows({
        viewName: 'order_settlements_view',
        params,
        mapRow: mapSettlement,
        orderBy: [
          { column: 'sale_date', ascending: false },
          { column: 'created_at', ascending: false },
        ],
      }),
  );

export const fetchDailySalesReportRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<SalesReportSummary>>> =>
  fetchCachedRows(
    buildCacheKey('dailySales', params),
    params.forceRefresh,
    () =>
      loadReportRows({
        viewName: 'sales_summary_view',
        params,
        mapRow: mapSummary,
        orderBy: [
          { column: 'sale_date', ascending: false },
          { column: 'outlet_name', ascending: true },
        ],
      }),
  );

export const fetchPaymentMethodDailySummaryRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<PaymentMethodDailySummary>>> =>
  fetchCachedRows(
    buildCacheKey('paymentMethods', params),
    params.forceRefresh,
    () => loadPaymentMethodDailySummaryRows(params),
  );

export const fetchDepartmentSalesReportRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<DepartmentSalesSummary>>> =>
  fetchCachedRows(
    buildCacheKey('departments', params),
    params.forceRefresh,
    () =>
      loadReportRows({
        viewName: 'department_sales_summary_view',
        params,
        mapRow: mapDepartmentSummary,
        orderBy: [
          { column: 'sale_date', ascending: false },
          { column: 'department_name', ascending: true },
        ],
      }),
  );

export const fetchItemsReportRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<ItemSalesSummaryView>>> =>
  fetchCachedRows(buildCacheKey('items', params), params.forceRefresh, () =>
    loadItemSalesSummaryRows(params),
  );

export const fetchDiscountsReportRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<SalesReportDiscountView>>> =>
  fetchCachedRows(buildCacheKey('discounts', params), params.forceRefresh, () =>
    loadReportRows({
      viewName: 'sales_discounts_view',
      params,
      mapRow: mapDiscount,
      orderBy: [
        { column: 'sale_date', ascending: false },
        { column: 'created_at', ascending: false },
      ],
    }),
  );

export const fetchCreditorsReportRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<SalesReportCreditorView>>> =>
  fetchCachedRows(buildCacheKey('creditors', params), params.forceRefresh, () =>
    loadReportRows({
      viewName: 'sales_creditor_settlements_view',
      params,
      mapRow: mapCreditor,
      orderBy: [
        { column: 'sale_date', ascending: false },
        { column: 'created_at', ascending: false },
      ],
    }),
  );

export const fetchCreditBillsRows = async (
  params: ReportQueryParams,
): Promise<ApiResponse<CachedReportRows<SalesReportCreditorView>>> => {
  const clientId =
    typeof params.filters?.clientId === 'string'
      ? params.filters.clientId
      : null;

  return fetchCachedRows(
    buildCacheKey('credit-bills', params),
    params.forceRefresh,
    async () => {
      const offset = params.offset ?? 0;
      const limit = params.limit ?? DEFAULT_REPORT_PAGE_SIZE;

      let query = applyScope(
        supabase
          .from('sales_creditor_settlements_view')
          .select('*')
          .eq('payment_method_name', 'CREDIT')
          .gte('sale_date', params.startDate)
          .lte('sale_date', params.endDate),
        params.companyId,
        params.outletId,
      );

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      query = query
        .order('sale_date', { ascending: false })
        .order('created_at', { ascending: false });

      const { data, error } = await query.range(offset, offset + limit);

      if (error) {
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          rows: (data || []).slice(0, limit).map(mapCreditor),
          hasMore: (data || []).length > limit,
        },
      };
    },
  );
};

export const fetchReportOrderDetails = async (
  orderId: string,
  forceRefresh?: boolean,
): Promise<ApiResponse<CachedOrderDetails>> =>
  fetchCachedOrderDetails(
    `order-details::${orderId}`,
    forceRefresh,
    async () => {
      const [
        orderResponse,
        itemsResponse,
        settlementsResponse,
        discountsResponse,
      ] = await Promise.all([
        supabase
          .from('sales_orders_view')
          .select('*')
          .eq('order_id', orderId)
          .maybeSingle(),
        supabase
          .from('sales_items_view')
          .select('*')
          .eq('order_id', orderId)
          .order('created_at', { ascending: true }),
        supabase
          .from('order_settlements_view')
          .select('*')
          .eq('order_id', orderId)
          .order('created_at', { ascending: true }),
        supabase
          .from('sales_discounts_view')
          .select('*')
          .eq('order_id', orderId)
          .order('created_at', { ascending: true }),
      ]);

      if (orderResponse.error) {
        return {
          success: false,
          error: orderResponse.error.message,
        };
      }

      if (!orderResponse.data) {
        return {
          success: false,
          error: 'Order details could not be found for this record.',
        };
      }

      if (itemsResponse.error) {
        return {
          success: false,
          error: itemsResponse.error.message,
        };
      }

      if (settlementsResponse.error) {
        return {
          success: false,
          error: settlementsResponse.error.message,
        };
      }

      if (discountsResponse.error) {
        return {
          success: false,
          error: discountsResponse.error.message,
        };
      }

      return {
        success: true,
        data: {
          order: mapOrder(orderResponse.data),
          items: (itemsResponse.data || []).map(mapItem),
          settlements: (settlementsResponse.data || []).map(mapSettlement),
          discounts: (discountsResponse.data || []).map(mapDiscount),
        },
      };
    },
  );

export const fetchItemReportDepartmentOptions = async (
  companyId: number,
  outletId: string | null,
  forceRefresh?: boolean,
): Promise<ApiResponse<ReportFilterOption[]>> => {
  const cacheKey = [
    'item-report-departments',
    companyId,
    outletId || 'all',
  ].join('::');

  const cached = filterOptionsCache.get(cacheKey);
  if (
    cached &&
    !forceRefresh &&
    Date.now() - cached.fetchedAt <= REPORT_CACHE_TTL_MS
  ) {
    return {
      success: true,
      data: cached.data,
    };
  }

  let query = supabase
    .from('departments')
    .select('id, name')
    .eq('status', 'active')
    .order('name', { ascending: true });

  if (outletId) {
    query = query.eq('outlet_id', outletId);
  }

  const { data, error } = await query;

  if (error) {
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }

  const options = ((data || []) as Array<{ id: string; name: string }>).map(
    (department) => ({
      id: department.id,
      name: department.name,
    }),
  );

  filterOptionsCache.set(cacheKey, {
    data: options,
    fetchedAt: Date.now(),
  });

  return {
    success: true,
    data: options,
  };
};

export const fetchItemSalesSummaryDetails = async (
  summary: ItemSalesSummaryView,
  forceRefresh?: boolean,
): Promise<ApiResponse<CachedItemSalesDetails>> =>
  fetchCachedItemSalesDetails(
    `item-sales-details::${summary.id}`,
    forceRefresh,
    async () => {
      const settledOrdersResponse = await supabase
        .from('sales_orders_view')
        .select('order_id')
        .eq('sale_date', summary.sale_date)
        .eq('outlet_id', summary.outlet_id)
        .gt('settlement_count', 0);

      if (settledOrdersResponse.error) {
        return {
          success: false,
          error: settledOrdersResponse.error.message,
        };
      }

      const settledOrderIds = (settledOrdersResponse.data || [])
        .map((row: { order_id: string | null }) => row.order_id)
        .filter((value): value is string => Boolean(value));

      if (settledOrderIds.length === 0) {
        return {
          success: true,
          data: {
            summary,
            sold_by: [],
            records: [],
          },
        };
      }

      const { data, error } = await supabase
        .from('sales_items_view')
        .select('*')
        .eq('sale_date', summary.sale_date)
        .eq('outlet_id', summary.outlet_id)
        .eq('menu_item_id', summary.menu_item_id)
        .eq('unit_price', summary.unit_price)
        .neq('status', 'CANCELLED')
        .in('order_id', settledOrderIds)
        .order('created_at', { ascending: true });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const records = (data || []).map(mapItem);
      const soldByMap = new Map<
        string,
        ItemSalesSummaryDetails['sold_by'][0]
      >();

      records.forEach((record) => {
        const key = record.added_by.id || 'system';
        const current = soldByMap.get(key);

        if (current) {
          current.quantity_sold += record.quantity;
          current.gross_amount += record.total_amount;
          current.record_count += 1;
          return;
        }

        soldByMap.set(key, {
          user_id: record.added_by.id,
          first_name: record.added_by.first_name,
          last_name: record.added_by.last_name,
          quantity_sold: record.quantity,
          gross_amount: record.total_amount,
          record_count: 1,
        });
      });

      const soldBy = Array.from(soldByMap.values()).sort((left, right) => {
        if (right.quantity_sold !== left.quantity_sold) {
          return right.quantity_sold - left.quantity_sold;
        }

        const leftName =
          `${left.first_name || ''} ${left.last_name || ''}`.trim();
        const rightName =
          `${right.first_name || ''} ${right.last_name || ''}`.trim();
        return leftName.localeCompare(rightName);
      });

      return {
        success: true,
        data: {
          summary,
          sold_by: soldBy,
          records,
        },
      };
    },
  );

const isElectron = (): boolean =>
  typeof window !== 'undefined' && !!window.electron?.ipcRenderer;

const escapeCsvCell = (val: unknown): string => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  // Prefix formula-injection characters to match Electron's escape_formulas option
  const safe =
    s.startsWith('=') ||
    s.startsWith('+') ||
    s.startsWith('-') ||
    s.startsWith('@')
      ? `'${s}`
      : s;
  return safe.includes(',') || safe.includes('"') || safe.includes('\n')
    ? `"${safe.replace(/"/g, '""')}"`
    : safe;
};

const buildWebCsvContent = (request: ExportReportCsvRequest): string => {
  const header = request.columns.map((c) => escapeCsvCell(c.header)).join(',');
  const lines = request.rows.map((row) =>
    request.columns.map((c) => escapeCsvCell(row[c.key])).join(','),
  );
  // BOM + CSV to match Electron's bom: true output
  return `\uFEFF${[header, ...lines].join('\n')}`;
};

const downloadCsvInBrowser = (fileName: string, content: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${fileName}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const exportReportCsv = async (
  request: ExportReportCsvRequest,
): Promise<ApiResponse<ExportReportCsvResponse>> => {
  if (!isElectron()) {
    try {
      const content = buildWebCsvContent(request);
      downloadCsvInBrowser(request.fileName, content);
      return { success: true, data: { filePath: `${request.fileName}.csv` } };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to export report CSV',
        data: undefined,
      };
    }
  }

  try {
    const response = (await window.electron.ipcRenderer.invoke(
      REPORTS_IPC_CHANNELS.EXPORT_REPORT_CSV,
      request,
    )) as ApiResponse<ExportReportCsvResponse>;

    if (!response?.success || !response.data) {
      return {
        success: false,
        error: response?.error || 'Failed to export report CSV',
        data: undefined,
      };
    }

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to export report CSV',
      data: undefined,
    };
  }
};

export const clearReportsCache = (): void => {
  reportCache.clear();
  inFlightCache.clear();
  filterOptionsCache.clear();
  orderDetailsCache.clear();
  orderDetailsInFlight.clear();
  itemSalesDetailsCache.clear();
  itemSalesDetailsInFlight.clear();
};
